import { Lock } from "lucide-react";
import { PatientClosing } from "@/components/patient/PatientClosing";
import { LanguageToggle } from "@/components/LanguageToggle";
import { HomeLink } from "@/components/exo/driversExoShared";
import { useDepot } from "@/lib/useDepot";
import { DepotActions } from "@/components/autograt/DepotActions";
import { Btn } from "@/components/autograt/agShared";
import { useEntourageSeance } from "@/components/exo/useEntourageSeance";
import { DriversPdf, DRIVERS_PDF_ID, DRIVERS_PDF_FOOTER } from "@/components/exo/DriversPdf";

export const Synthese = ({ entries, ranking, onRestart, entourageToken, inviteLang, setInviteLang, onInvite, onOpenEntourage }) => {
  const { proches, praticienRanking } = useEntourageSeance(entourageToken);
  const d = useDepot("m2hat_drivers_depot");
  const filename = `drivers-relationnels-${new Date().toISOString().slice(0, 10)}.pdf`;
  const el = () => document.getElementById(DRIVERS_PDF_ID);

  return (
    <div className="animate-fade-up" data-testid="exo-synthese">
      <HomeLink />
      <div className="text-center mb-8">
        <h2 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] mb-4">Votre travail est terminé</h2>
        <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto">Merci pour vos réflexions. Votre classement et vos situations ont été rassemblés dans un document destiné à votre praticien.</p>
      </div>

      <section className="rounded-3xl bg-white/80 border-2 border-[var(--gold)] p-5 sm:p-6 mb-6" data-testid="exo-reserve-praticien">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--gold)] mb-2 inline-flex items-center gap-1.5"><Lock size={13} /> Résultats réservés au praticien</div>
        <p className="text-[var(--ink)] leading-relaxed">La synthèse (classement, réflexions, premiers pas{proches.length ? ", regards croisés de vos proches" : ""}) sera lue et travaillée avec votre praticien en séance. Vous pouvez la télécharger pour la lui remettre, ou la lui transmettre directement s'il vous a donné un lien de dépôt.</p>
      </section>

      <DepotActions d={d} prefix="exo" downloadLabel="Télécharger le document pour mon praticien" pro="praticien"
        onDownload={() => d.download(el(), filename, DRIVERS_PDF_FOOTER)} onShare={() => d.share(el(), filename, DRIVERS_PDF_FOOTER, "Travail autour des drivers")}
        extra={<Btn secondary onClick={onRestart} testid="exo-btn-restart">Recommencer</Btn>} />

      <PatientClosing lang="fr" />

      <div className="rounded-3xl border border-dashed border-[var(--gold-light)] bg-white/50 p-6 sm:p-7 mt-8 text-center no-print" data-testid="exo-entourage-cta">
        <p className="text-xs uppercase tracking-[0.15em] text-[var(--gold-light)] mb-1">Aller plus loin</p>
        <h3 className="font-serif-title text-2xl text-[var(--gold)] mb-1">Le regard d'un proche</h3>
        <p className="text-[var(--ink)] mb-4 max-w-xl mx-auto">Invitez un ou plusieurs proches à décrire comment ils perçoivent vos drivers et à proposer leur propre classement. Leurs regards rejoindront le document destiné à votre praticien.</p>
        {entourageToken ? (
          <Btn onClick={onOpenEntourage} testid="exo-btn-open-entourage">{proches.length ? `${proches.length} proche${proches.length > 1 ? "s ont" : " a"} répondu — gérer les invitations` : "Voir mon lien d'invitation"}</Btn>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-2" data-testid="exo-entourage-lang"><span className="text-sm text-[var(--ink-soft)]">Langue du proche :</span><LanguageToggle lang={inviteLang} onChange={setInviteLang} /></div>
            <Btn onClick={onInvite} testid="exo-btn-invite-entourage">Inviter un ou des proches</Btn>
          </div>
        )}
      </div>

      <DriversPdf entries={entries} ranking={ranking} proches={proches} praticienRanking={praticienRanking} />
    </div>
  );
};
